// ===================== 类型定义 =====================


// 权限枚举常量
const ACTION_CONFIG_ENUM = {
  'add': { value: 'add', label: '新增' },
  'delete': { value: 'delete', label: '删除' },
  'edit': { value: 'edit', label: '编辑' },
  'query': { value: 'query', label: '查询' },
  'get': { value: 'get', label: '详情' },
  'enable': { value: 'enable', label: '启用' },
  'disable': { value: 'disable', label: '禁用' },
  'import': { value: 'import', label: '导入' },
  'export': { value: 'export', label: '导出' },
}



// 类型守卫：校验字符串是否为合法权限key
export function isPermissionKey(key) {
  return Object.prototype.hasOwnProperty.call(ACTION_CONFIG_ENUM, key)
}

// ===================== 工具函数 =====================
/**
 * 根据actionKey获取权限基础信息
 * @param actionKey 权限标识
 */
function getActionDetail(actionKey){
  if (isPermissionKey(actionKey)) {
    return {
      checked: false,
      ...ACTION_CONFIG_ENUM[actionKey],
    }
  }
  return undefined
}

/**
 * 替换路由meta标题
 * @param route 路由信息
 * @param replacerList 需要替换的配置信息
 */
function replaceRouteName(route, replacerList) {
  if (!replacerList || replacerList.length === 0 || !route || !route.name || !route.meta)
    return

  const findItem = replacerList.find(item => item.name === route.name)
  if (findItem) {
    route.meta.title = findItem.title
  }
}
/**
 * 替换外部跳转
 * @param list
 * @param getSkipUrl
 */
export function replaceSkipPath(list, getSkipUrl) {
  if (list && list.length > 0) {
    list.forEach((item) => {
      if (item && item.children && item.children.length > 0) {
        replaceSkipPath(item.children, getSkipUrl)
      }
      else if (item && item.name && (!item.children || item.children.length < 1)) {
        if (getSkipUrl && typeof getSkipUrl === 'function') {
          const skipUrl = getSkipUrl(item.name)
          if (skipUrl) {
            item.path = skipUrl
          }
        }
      }
    })
  }
}
// ===================== 主工具类 =====================
export class PermissionHelper {
  static getActionConfigDetail() {
    const config = {}
    Object.keys(ACTION_CONFIG_ENUM).forEach((key) => {
      config[key] = true
    })
    return config
  }

  /**
   * 递归生成权限树形结构（支持三级/无限层级）
   * @param routeList 路由数组
   * @param disableMenuList 禁用菜单name数组
   * @param replacerList 标题替换配置
   * @returns 树形options
   */
  static getPermissionMenuOptions(
    routeList,
    disableMenuList,
    replacerList,
  ) {
    const options= []
    routeList.forEach((route) => {
      const routeName = route.name?.toString() || ''
      const disable = disableMenuList && disableMenuList.length > 0 && disableMenuList.includes(routeName)
      if (disable || route?.meta?.hidePermissionTree)
        return

      replaceRouteName(route, replacerList)

      const routeOptions = {
        permissionId: routeName,
        required: !!route.required,
        title: route.meta?.title || '',
        permissionList: [],
        checkAll: false,
      }

      // 处理当前节点按钮权限
      if (route.meta?.actions && route.meta.actions.length > 0) {
        routeOptions.actions = route.meta.actions.map((actionItem) => {
          const actionDetail = getActionDetail(actionItem.key)
          const permissionId = actionItem.permissionId || actionItem.key
          return {
            ...actionItem,
            ...actionDetail,
            permissionId,
            checked: false,
          }
        })
      }

      // 递归处理子菜单
      if (route.children && route.children.length > 0) {
        routeOptions.children = PermissionHelper.getPermissionMenuOptions(route.children, disableMenuList, replacerList)

        // 初始化本级permissionList：仅required强制勾选的子菜单
        routeOptions.children.forEach((child) => {
          if (child.required) {
            routeOptions.permissionList.push(child.permissionId)
          }
        })
        // 计算当前分组是否全选
        routeOptions.checkAll = routeOptions.children.length > 0 && routeOptions.permissionList.length === routeOptions.children.length
      }

      options.push(routeOptions)
    })

    // 过滤掉无下级空节点，避免空白行
    return options.filter(opItem => !opItem.children || opItem.children.length > 0)
  }

  /**
   * 递归提取所有选中权限ID（菜单ID + 按钮actionID，修复三级+按钮丢失问题）
   * @param rolePermissionList 树形options数组
   * @returns 一维权限ID数组
   */
  static getPermissionList(rolePermissionList){
    const permissionList= []
    if (!rolePermissionList || !rolePermissionList.length)
      return permissionList

    rolePermissionList.forEach((item) => {
      // 1、收集当前节点直接勾选的子菜单ID（二级/三级菜单）
      if (item.permissionList && item.permissionList.length) {
        permissionList.push(...item.permissionList)
      }

      // 2、收集当前节点按钮actions选中的权限ID
      if (item.actions && item.actions.length) {
        const checkedActions = item.actions.filter(act => item.permissionList?.includes(act.permissionId))
        permissionList.push(...checkedActions.map(act => act.permissionId))
      }

      // 3、递归处理下级菜单
      if (item.children && item.children.length) {
        const childIds = PermissionHelper.getPermissionList(item.children)
        permissionList.push(...childIds)
      }
    })

    // 去重返回
    return [...new Set(permissionList)]
  }

  /**
   * 过滤异步路由（页面动态渲染侧边栏菜单）
   * @param routerMap 初始路由数据
   * @param roles 当前角色权限树
   * @param ignorePermission 忽略权限标记
   * @param disableMenuList 禁用路由name列表
   * @param replacerList 标题替换配置表
   * @returns 过滤后可展示路由
   */
  static filterAsyncRouter(
    routerMap,
    roles,
    ignorePermission,
    disableMenuList,
    replacerList,
  ) {
    const newRouters = []
    const allPermissionIds = PermissionHelper.getPermissionList(roles.authority || [])

    routerMap.forEach((route) => {
      const routeName = route.name?.toString() || ''
      replaceRouteName(route, replacerList)

      // 404页面直接放行
      if (route.path === '*') {
        newRouters.push({ ...route })
        return
      }

      // 有子路由，递归过滤子节点
      if (route.children && route.children.length > 0) {
        const childIgnore = ignorePermission || !!route.ignorePermissions
        const childrenRoutes = PermissionHelper.filterAsyncRouter(
          route.children,
          roles,
          childIgnore,
          disableMenuList,
          replacerList,
        )
        if (childrenRoutes.length > 0) {
          newRouters.push({ ...route, children: childrenRoutes })
        }
        return
      }

      // 无子路由，判断是否拥有权限
      const isDisable = disableMenuList?.includes(routeName)
      const isIgnore = ignorePermission || !!route.ignorePermissions
      const hasPerm = allPermissionIds.includes(routeName)

      if (route.name && !isDisable && (isIgnore || hasPerm)) {
        newRouters.push({ ...route })
      }
    })

    // 处理父菜单隐藏逻辑：子菜单全部隐藏则父菜单隐藏
    newRouters.forEach((item) => {
      if (!item.children || !item.children.length)
        return
      let hidden = true
      for (const child of item.children) {
        if (!child.hidden) {
          hidden = false
          break
        }
      }
      item.hidden = hidden
    })
    return newRouters
  }
  static collectEmptyIdFullInfo(treeData) {
    const result = [];
    if (!Array.isArray(treeData)) return result;

    function traverse(nodes) {
      nodes.forEach(node => {
        if (node.permissionId === "") {
          // 展开原节点所有属性，并强制把 children 设为 undefined（从而不包含在结果里）
          result.push({ ...node, children: undefined });
        }

        if (Array.isArray(node.children) && node.children.length > 0) {
          traverse(node.children);
        }
      });
    }

    traverse(treeData);
    return result;
  }
}
